import React, { useMemo, useEffect, useState } from "react";
import SelectUserType from "./SelectUserType";
import OnboardingForm from "./OnboardingForm";
import SignupNavigation from "./SignupNavigation";
import OnboardingSidebar from "./OnboardingSidebar";
import ProgressBar from '../ProgressBar/ProgressBar';
import { createLearner, createFacilitator, updateProfile, updateOrgSetup, updateGuardianPermission } from "@/api/onboarding";
import { getCurrentUser } from "@/api/auth";
import { getOnboarding } from "@/api/onboarding";
import { getByPath } from "@/utils/getByPath";
import { evaluateConditions } from "@/utils/evaluateConditions";
import { isValidEmail } from "@/utils/validateEmail";
import learnerSignupFlow from "./constants/onboarding-flows/learnerSignup.json";
import facilitatorSignupFlow from "./constants/onboarding-flows/facilitatorSignup.json";
import facilitatorPersonalDataFlow from "./constants/onboarding-flows/facilitatorPersonalData.json";
import joinOrInitOrganization from "./constants/onboarding-flows/joinOrInitOrganization.json";
import learnerPersonalDataFlow from "./constants/onboarding-flows/learnerPersonalData.json";
import guardianPermissionFlow from "./constants/onboarding-flows/guardianPermission.json"
import guardianPermissionLink from "./constants/onboarding-flows/guardianPermissionLink.json"
import signupSuccessFlow from "./constants/onboarding-flows/signupSuccess.json"
import waitForAccountApproval from "./constants/onboarding-flows/waitForAccountApproval.json"

const skippable_content = {
  "skippablePhases": [
    {
      "phaseKey": "yourInterests",
      "skippableSteps": "all"
    },
    {
      "phaseKey": "guardian",
      "skippableSteps": [5, 6]
    }
  ]
}

type InitialData = {
  userType?: "learner" | "facilitator" | "guardian";
  signupCompleted: boolean;
  language?: string;
  username?: string;
  password?: string;
  success?: boolean;
};

type Props = {
  initialData?: InitialData;
};


type StepConfig = {
  type?: "image-pick" | "text" | "dropdown" | "radio" | string;
  question?: string;
  inputs?: Array<any>;
  inputGroup?: string;
  [k: string]: any;
};

type Values = Record<string, any>;

function validateStep(stepConfig: StepConfig, data: Values): boolean {
  if (!stepConfig) return false;

  let isValid = true;

  stepConfig.layout?.forEach((block: any) => {
    if (block.blockType !== "input") return;

    const groupKey = block.inputGroup;
    const fieldsGroupKey = block.props.fieldsGroupKey || null


    block.props.fields.forEach((field: any) => {

      if (field.showIf && !evaluateConditions(field.showIf, data)) return;

      let value;
      let path = "";

      if (!field.key) {
        if (groupKey && fieldsGroupKey) {
          path = `${groupKey}.${fieldsGroupKey}`;
        } else if (groupKey) {
          path = groupKey;
        } else if (fieldsGroupKey) {
          path = fieldsGroupKey;
        } else {
          path = "";
        }
      } else {

        path =
          groupKey && fieldsGroupKey
            ? `${groupKey}.${fieldsGroupKey}`
            : groupKey
              ? `${groupKey}.${field.key}`
              : fieldsGroupKey
                ? `${fieldsGroupKey}${fieldsGroupKey === field.key ? "" : `.${field.key}`}`
                : field.key;
      }

      value = path ? getByPath(data, path) : undefined;

      // Required check
      if (!field.isOptional && (value === undefined || value === "" || value === null || value.length === 0)) {
        isValid = false;
      }


      // Min/Max length
      if (field.minLength && value && value.length < field.minLength) {
        isValid = false;
      }
      if (field.maxLength && value && value.length > field.maxLength) {
        isValid = false;
      }

      if (field.forceTrue && value !== true) {
        isValid = false;
      }

      // Validate Email
      if (field.subtype === "email") {
        isValid = isValidEmail(value);
      }

      // Password confirmation
      if (field.key === "password_confirmation") {
        const password = groupKey
          ? data[groupKey]?.["password"]
          : data["password"];
        if (password !== value) {
          isValid = false;
        }
      }

    });
  });

  return isValid;
}

// Flow builder
function buildFlow(initialData: InitialData, userType?: string, guardianPresent?: string, organizationId?: string): StepConfig[] {
  const flows: StepConfig[] = [];

  // If no type and not signed up → don't load anything yet
  if (!userType && !initialData.signupCompleted) {
    return flows;
  }

  switch (userType || initialData.userType) {
    case "guardian":
      flows.push(...(guardianPermissionFlow as StepConfig[]));
      break;

    case "learner":
      if (initialData.signupCompleted && initialData.password) {
        flows.push(...(signupSuccessFlow as StepConfig[]));
      }

      if (!initialData.signupCompleted) {
        flows.push(...(learnerSignupFlow as StepConfig[]));
      }

      flows.push(...(learnerPersonalDataFlow as StepConfig[]));

      if (guardianPresent === "true") {
        flows.push(...(guardianPermissionFlow as StepConfig[]));
      }

      if (guardianPresent === "false") {
        flows.push(...(guardianPermissionLink as StepConfig[]));
      }

      break;

    case "facilitator":
      if (initialData.signupCompleted && initialData.password) {
        flows.push(...(signupSuccessFlow as StepConfig[]));
      }

      if (!initialData.signupCompleted) {
        flows.push(...(facilitatorSignupFlow as StepConfig[]));
      }

      if (organizationId === undefined) {
        flows.push(...(joinOrInitOrganization as StepConfig[]));
      }

      flows.push(...(facilitatorPersonalDataFlow as StepConfig[]));
      flows.push(...(waitForAccountApproval as StepConfig[]));

      break;
  }

  return flows;
}


const OnboardingFlow = ({ initialData }) => {
  const [step, setStep] = useState<number>(0);
  const [showSkipButton, setShowSkipButton] = useState<boolean>(false);

  const [userType, setUserType] = useState<"learner" | "facilitator" | "guardian" | null>(
    initialData?.userType ?? null
  );

  const [completedSteps, setCompletedSteps] = useState<string[]>([]);

  const [data, setData] = useState<Record<string, any>>({});

  const [categories, setCategories] = useState<
    Array<{
      id: number;
      name: string;
      name_fr?: string;
      description?: string;
      description_fr?: string;
      icon_class?: string;
      slug: string;
    }>
  >([]);

  const [showBackButton, setShowBackButton] = useState<boolean>(true)

  const [showContinueButton, setShowContinueButton] = useState<boolean>(true)

  const [showSignupButton, setShowSignupButton] = useState<boolean>(true)

  const [currentPhase, setCurrentPhase] = useState<Phase | null>(null);

  const flow: StepConfig[] = useMemo(
    () =>
      buildFlow(initialData || {}, userType || undefined, data.guardian_present || "false", data.organization?.id),
    [initialData, userType, data.guardian_present, data.organization?.id]
  );

  useEffect(() => {
    async function init() {
      const user = await getCurrentUser();

      if (!user) return;
      try {
        const record = await getOnboarding();

        if (record?.steps) {
          const completed = new Set(record.steps as string[]);
          setCompletedSteps(Array.from(completed));

          // Now use the memoized flow to find first incomplete
          const firstIncompleteIndex = flow.findIndex(
            (s) => s.id === "guardian-present" || !completed.has(s.id as string)
          );

          setStep(firstIncompleteIndex !== -1 ? firstIncompleteIndex : flow.length - 1);
        }

        if (record?.categories) {
          setCategories(record.categories);
        }

        // Hydrate data from API
        if (user.profile_type === "Learner") {
          setData({
            learner: record?.learner || {},
            user: record?.user || {},
            guardian: record?.guardian || {},
            guardianPermissionLink: record?.guardian_permission_link || ""
          });
        }

        if (user.profile_type === "Facilitator") {
          setData({
            facilitator: record?.facilitator || {},
            user: record?.user || {},
            organization: record?.organization || {},
          });
        }
      } catch (err) {
      }
    }

    init();
  }, [data.organization?.id]);

  // Build the list of steps that are currently visible based on answers
  const visibleFlow: StepConfig[] = useMemo(() => {
    return flow.filter(
      (s) =>
        s.id === "guardian_present" ||
        evaluateConditions(s.visibleIf, data) ||
        !s.visibleIf
    );
  }, [flow, data]);

  // Ensure current `step` stays within bounds (especially after answers change)
  useEffect(() => {
    if (!userType && !initialData?.signupCompleted) {
      // no user type selected, signup not completed yet
      setStep(-1);
      return;
    }

    if (userType && step === -1) {
      // user just selected a type → start flow at step 0
      setStep(0);
      return;
    }

    // Allow step === visibleFlow.length to show completion
    if (visibleFlow.length === 0 && step !== 0) {
      setStep(0);
    } else if (step > visibleFlow.length) {
      // Only clamp if we've gone *beyond* completion
      setStep(visibleFlow.length);
    }
  }, [userType, initialData?.signupCompleted, visibleFlow.length, step]);

  // Current step config pulled from the *visible* flow
  const stepConfig: StepConfig | undefined = visibleFlow[step];

  useEffect(() => {
    if (!currentPhase || !stepConfig) {
      setShowSkipButton(false);
      return;
    }

    // Find configuration for current phase
    const phaseConfig = skippable_content.skippablePhases.find(
      (config) => config.phaseKey === currentPhase.key
    );

    if (!phaseConfig) {
      setShowSkipButton(false);
      return;
    }

    // Check if entire phase is skippable or if current step number is skippable
    const isSkippable =
      phaseConfig.skippableSteps === "all" ||
      (Array.isArray(phaseConfig.skippableSteps) &&
        phaseConfig.skippableSteps.includes(currentPhase.stepNumber));

    setShowSkipButton(isSkippable);
  }, [currentPhase, stepConfig]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const orgCode = url.searchParams.get("organization_code");

    // Build a clean URL that keeps only organization_code
    const cleanUrl = orgCode
      ? `${url.pathname}?organization_code=${orgCode}`
      : url.pathname;

    // Only replace if the URL actually changes
    if (cleanUrl !== url.href.replace(url.origin, "")) {
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  useEffect(() => {
    const orgCode = initialData?.organization_code;
    if (orgCode) {
      setData(prev => ({ ...prev, organization_code: orgCode }));
    }
  }, [initialData?.organization_code]);

  const handleSkip = async () => {
    if (!stepConfig) return;

    try {
      const fillerData = { ...data };

      stepConfig.layout?.forEach((block: any) => {
        if (block.blockType !== "input") return;

        const groupKey = block.inputGroup;
        const fieldsGroupKey = block.props.fieldsGroupKey || null;

        block.props.fields.forEach((field: any) => {
          if (field.showIf && !evaluateConditions(field.showIf, fillerData)) return;

          let path = "";
          if (!field.key) {
            if (groupKey && fieldsGroupKey) {
              path = `${groupKey}.${fieldsGroupKey}`;
            } else if (groupKey) {
              path = groupKey;
            } else if (fieldsGroupKey) {
              path = fieldsGroupKey;
            }
          } else {
            path =
              groupKey && fieldsGroupKey
                ? `${groupKey}.${fieldsGroupKey}`
                : groupKey
                  ? `${groupKey}.${field.key}`
                  : fieldsGroupKey
                    ? `${fieldsGroupKey}${fieldsGroupKey === field.key ? "" : `.${field.key}`}`
                    : field.key;
          }

          const currentValue = path ? getByPath(fillerData, path) : undefined;

          // Only add filler if no value exists
          if (currentValue === undefined || currentValue === "" || currentValue === null) {
            let fillerValue;

            // Determine appropriate filler based on field type
            if (field.type === "checkbox" || field.type === "checkboxGroup") {
              fillerValue = ["N/A"];
            } else if (field.type === "radio") {
              fillerValue = "N/A";
            } else if (field.type === "dropdown" || field.type === "select") {
              fillerValue = "N/A";
            } else if (field.forceTrue) {
              fillerValue = false;
            } else if (field.subtype === "email") {
              fillerValue = "unanswered@na.com";
            } else {
              fillerValue = "N/A"; // Default for text inputs
            }

            // Set the filler value using the same structure as handleChange
            if (groupKey && fieldsGroupKey) {
              if (!fillerData[groupKey]) fillerData[groupKey] = {};
              if (field.key) {
                if (!fillerData[groupKey][fieldsGroupKey]) fillerData[groupKey][fieldsGroupKey] = {};
                fillerData[groupKey][fieldsGroupKey][field.key] = fillerValue;
              } else {
                fillerData[groupKey][fieldsGroupKey] = fillerValue;
              }
            } else if (groupKey) {
              if (!fillerData[groupKey]) fillerData[groupKey] = {};
              fillerData[groupKey][field.key] = fillerValue;
            } else if (fieldsGroupKey) {
              if (field.key) {
                if (!fillerData[fieldsGroupKey]) fillerData[fieldsGroupKey] = {};
                fillerData[fieldsGroupKey][field.key] = fillerValue;
              } else {
                fillerData[fieldsGroupKey] = fillerValue;
              }
            } else {
              fillerData[field.key] = fillerValue;
            }
          }
        });
      });

      // Update local state with filler data
      setData(fillerData);

      // Mark this step as completed in the backend with filler data
      if (userType === "learner") {
        await updateProfile({
          learner: fillerData["learner"] || {},
          user: fillerData["user"] || {},
          guardian: fillerData["guardian"] || {},
          contact_preferences: fillerData["contact_preferences"] || {},
          onboarding_step: stepConfig.id,
        });
      } else if (userType === "facilitator") {
        await updateProfile({
          facilitator: fillerData["facilitator"] || {},
          user: fillerData["user"] || {},
          contact_preferences: fillerData["contact_preferences"] || {},
          onboarding_step: stepConfig.id,
        });
      } else if (userType === "guardian") {
        const token = window.location.pathname.split("/").pop();
        await updateGuardianPermission({
          guardian_signup_token: token,
          learner: fillerData["learner"] || {},
          user: fillerData["user"] || {},
          guardian: fillerData["guardian"] || {},
          contact_preferences: fillerData["contact_preferences"] || {},
          onboarding_step: stepConfig.id,
        });
      }

      // Mark as completed locally
      setCompletedSteps(prev =>
        prev.includes(stepConfig.id) ? prev : [...prev, stepConfig.id]
      );

      // Move to next step
      setStep((s) => {
        const nextStep = s + 1;
        if (nextStep >= visibleFlow.length) {
          window.location.href = "/dashboard";
          return s;
        }
        return nextStep;
      });
    } catch (err: any) {
      alert(
        err.response?.data?.errors?.join(", ") ||
        "Something went wrong. Please try again."
      );
    }
  };

  const handleChange = (
    key: string,
    value: any,
    options?: {
      remove?: boolean;
      groupKey?: string; // section group (inputGroup)
      fieldsGroupKey?: string; // block group (fieldsGroupKey)
      toggle?: boolean; // add/remove from array
    }
  ) => {
    setData((prev) => {
      const next = structuredClone(prev) as Record<string, any>;

      const { groupKey, fieldsGroupKey, remove, toggle } = options || {};

      // Helper to ensure nested object exists
      const ensureObj = (obj: any, k: string) =>
        (obj[k] = obj[k] && typeof obj[k] === "object" ? obj[k] : {});

      // Resolve target container + finalKey and write op
      if (groupKey && fieldsGroupKey) {
        // data[groupKey][fieldsGroupKey] -> value/array/object
        const groupObj = ensureObj(next, groupKey);
        const fg = fieldsGroupKey;

        if (remove) {
          delete groupObj[fg];
          if (Object.keys(groupObj).length === 0) delete next[groupKey];
          return next;
        }

        // If toggle -> treat target as an array and add/remove `value`
        if (toggle) {
          const arr = Array.isArray(groupObj[fg]) ? groupObj[fg] : [];
          const exists = arr.includes(value);
          groupObj[fg] = exists
            ? arr.filter((v: any) => v !== value)
            : [...arr, value];
          return next;
        }

        // Otherwise: write { ...existing, [key]: value } if key provided,
        // or write primitive/array to [fieldsGroupKey] if no key.
        if (key) {
          const bucket =
            typeof groupObj[fg] === "object" && !Array.isArray(groupObj[fg])
              ? groupObj[fg]
              : {};
          groupObj[fg] = { ...bucket, [key]: value };
        } else {
          groupObj[fg] = value;
        }
        return next;
      }

      if (groupKey && !fieldsGroupKey) {
        // data[groupKey][key] -> value
        const groupObj = ensureObj(next, groupKey);
        if (remove) {
          delete groupObj[key];
          if (Object.keys(groupObj).length === 0) delete next[groupKey];
        } else if (toggle) {
          const arr = Array.isArray(groupObj[key]) ? groupObj[key] : [];
          const exists = arr.includes(value);
          groupObj[key] = exists
            ? arr.filter((v: any) => v !== value)
            : [...arr, value];
        } else {
          groupObj[key] = value;
        }
        return next;
      }

      if (!groupKey && fieldsGroupKey) {
        if (remove) {
          delete next[fieldsGroupKey];
        } else if (toggle) {
          const arr = Array.isArray(next[fieldsGroupKey])
            ? next[fieldsGroupKey]
            : [];
          const exists = arr.includes(value);
          next[fieldsGroupKey] = exists
            ? arr.filter((v: any) => v !== value)
            : [...arr, value];
        } else if (key) {
          const bucket =
            typeof next[fieldsGroupKey] === "object" &&
              !Array.isArray(next[fieldsGroupKey])
              ? next[fieldsGroupKey]
              : {};
          next[fieldsGroupKey] = { ...bucket, [key]: value };
        } else {
          next[fieldsGroupKey] = value;
        }
        return next;
      }

      // flat write
      if (options?.remove) {
        delete next[key];
      } else if (options?.toggle) {
        const arr = Array.isArray(next[key]) ? next[key] : [];
        const exists = arr.includes(value);
        next[key] = exists
          ? arr.filter((v: any) => v !== value)
          : [...arr, value];
      } else {
        next[key] = value;
      }

      return next;
    });
  };

  const next = async () => {
    if (!stepConfig) return;

    try {
      if (stepConfig.navigationType === "signup") {
        if (userType === "learner") {
          const result = await createLearner(data);

          if (result.redirect_url) {
            window.location.href = result.redirect_url;
            return;
          }
        } else if (userType === "facilitator") {
          const result = await createFacilitator(data);

          if (result.redirect_url) {
            window.location.href = result.redirect_url;
            return;
          }
        }
      } else if (stepConfig.navigationType === "success") {
        setCompletedSteps(prev =>
          prev.includes("success") ? prev : [...prev, "success"]
        );
      } else if (stepConfig.navigationType === "joinOrg") {
        const joinExisting = data["join_existing_organization"] === true || data["join_existing_organization"] === "true";

        const payload = {
          join_existing_organization: joinExisting,
          organization_code: joinExisting ? data["organization_code"] : "",
        };

        const response = await updateOrgSetup(payload);

        if (joinExisting === true) {
          setData({
            organization: response?.organization || {},
          });
        }


      } else if (stepConfig.navigationType === "initOrg") {
        const payload = {
          complete_org_registration: data["accept_terms"],
          organization: data["organization"]
        }
        const response = await updateOrgSetup(payload);

        if (data["accept_terms"] === true) {
          setData({
            organization: response?.organization || {},
          });
        }
      } else {
        if (userType === "learner") {
          const learnerData = data["learner"] || {};
          const userData = data["user"] || {};
          const guardianData = data["guardian"] || {};
          const contactPreferences = data["contact_preferences"] || {};

          const result = await updateProfile({
            learner: learnerData,
            user: userData,
            guardian: guardianData,
            contact_preferences: contactPreferences,
            onboarding_step: stepConfig.id,
          });

          setCompletedSteps(prev =>
            prev.includes(stepConfig.id) ? prev : [...prev, stepConfig.id]
          );

        } else if (userType === "facilitator") {
          const facilitatorData = data["facilitator"] || {};
          const userData = data["user"] || {};
          const contactPreferences = data["contact_preferences"] || {};

          const result = await updateProfile({
            facilitator: facilitatorData,
            user: userData,
            contact_preferences: contactPreferences,
            onboarding_step: stepConfig.id,
          });

          setCompletedSteps(prev =>
            prev.includes(stepConfig.id) ? prev : [...prev, stepConfig.id]
          );
        } else if (userType === "guardian") {
          const token = window.location.pathname.split("/").pop();

          const learnerData = data["learner"] || {};
          const userData = data["user"] || {};
          const guardianData = data["guardian"] || {};
          const contactPreferences = data["contact_preferences"] || {};

          const result = await updateGuardianPermission({
            guardian_signup_token: token,
            learner: learnerData,
            user: userData,
            guardian: guardianData,
            contact_preferences: contactPreferences,
            onboarding_step: stepConfig.id,
          });
        }
      }

      // Advance step if no redirect
      setStep((s) => {
        const nextStep = s + 1;
        if (nextStep >= visibleFlow.length) {
          window.location.href = "/dashboard";
          return s;
        }
        // Smooth scroll to top of onboarding container (or window) after step advances
        setTimeout(() => {
          const container = document.querySelector('.onboarding-form');
          if (container && 'scrollTo' in container) {
            (container as HTMLElement).scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 0);
        return nextStep;
      });
    } catch (err: any) {
      alert(
        err.response?.data?.errors?.join(", ") ||
        "Something went wrong. Please try again."
      );
    }
  }

  const prev = () => {
    if (step === 0) {
      setUserType(null);
      setStep(0);
      setData({});
    } else if (step > visibleFlow.length - 1) {
      // If we're on completion (step === visibleFlow.length), go back to last step
      setStep(Math.max(0, visibleFlow.length - 1));
    } else {
      setStep((s) => Math.max(0, s - 1));
    }
  };

  const isStepValid = stepConfig ? validateStep(stepConfig, data) : false;


  useEffect(() => {
    let shouldShow = true

    if (!((step === 0 && !initialData.signupCompleted) || step >= 1)) {
      shouldShow = false
    }
    setShowBackButton(shouldShow);
  }, [step, stepConfig, initialData.signupCompleted]);

  useEffect(() => {
    let shouldShowContinueButton = true
    let shouldShowSignupButton = false

    if (!stepConfig || stepConfig.hideContinueButton === true) {
      shouldShowContinueButton = false
    }

    if (stepConfig?.showSignupButton === true) {
      shouldShowSignupButton = true
    }
    setShowContinueButton(shouldShowContinueButton);
    setShowSignupButton(shouldShowSignupButton)
  }, [stepConfig]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          textAlign: "center",
        }}
      >
        {(!userType && !initialData.signupCompleted) ? (
          <div
            className="onboarding-content"
          >
            <SelectUserType onSelect={(type) => setUserType(type)} />
          </div>
        ) : (
          <div
            className="onboarding-form"
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              justifyContent: "center",
              gap: "2rem",
              width: "100%",
            }}
          >
            {/* Sidebar on the left */}
            <OnboardingSidebar
              userType={userType}
              sectionId={stepConfig?.id}
              completedSteps={completedSteps}
              onPhaseChange={setCurrentPhase}
            />

            {/* Main form content */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "3.12rem 2.5rem",
                gap: "2.81rem",
              }}
            >
              {initialData.signupCompleted &&
                <ProgressBar
                  title={currentPhase?.title}
                  value={currentPhase?.stepNumber}
                  total={currentPhase?.totalSteps}
                />
              }

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center"
                }}
              >
                <OnboardingForm
                  stepConfig={stepConfig}
                  initialData={initialData}
                  data={data}
                  categories={categories}
                  onChange={handleChange}
                  onNext={next}
                />

                <SignupNavigation
                  step={step}
                  onPrev={prev}
                  onNext={next}
                  onSkip={handleSkip}
                  disableNext={!isStepValid}
                  showBackButton={showBackButton}
                  showContinueButton={showContinueButton}
                  showSignupButton={showSignupButton}
                  showSkipButton={showSkipButton}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingFlow;
